/**
 * Utilities for working with CRM entity types.
 *
 * Shared utilities that can be used across the CRM library and other components.
 */

/**
 * Convert snake_case string to Title Case, removing ahoy_ prefix.
 */
function snakeCaseToTitleCase(str: string): string {
  return str
    .replace(/^ahoy_/, '')
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Get human-readable label for CRM entity type
 */
export function getEntityTypeLabel(entityType: string): string {
  switch (entityType) {
    case 'ahoy_contact':
      return 'Contact';
    case 'ahoy_company':
      return 'Company';
    case 'ahoy_deal':
      return 'Deal';
    default:
      return snakeCaseToTitleCase(entityType);
  }
}

/**
 * Sentence-form label for mid-sentence phrasing like "created this <label>" /
 * "on this <label>" in timeline copy.
 *
 * Common nouns are fully lowercased ("deal", "contact", "note"); types whose
 * label carries a brand name keep the proper noun's casing
 * ("Stripe subscription" — never "stripe subscription").
 *
 * Callers that already resolved a label (e.g. from the org's live schema via
 * `useSchemaLabels`) pass it as `resolvedLabel` so custom-type labels are
 * preserved; otherwise the static label is used.
 */
export function getEntityTypeSentenceLabel(
  entityType: string,
  resolvedLabel?: string
): string {
  if (entityType === 'ahoy_stripe_subscription') {
    return 'Stripe subscription';
  }
  return (resolvedLabel ?? getEntityTypeLabel(entityType)).toLowerCase();
}

/**
 * Pluralize an entity type label.
 *
 * Accepts the lowercase singular label returned by `getEntityTypeLabel()`
 * (e.g. "contact", "company", "deal") and returns its plural form.
 *
 * @example pluralizeEntityLabel('contact')  // "contacts"
 * @example pluralizeEntityLabel('company')  // "companies"
 * @example pluralizeEntityLabel('deal')     // "deals"
 */
export function pluralizeEntityLabel(label: string): string {
  if (label.endsWith('y') && !/[aeiou]y$/i.test(label)) {
    return `${label.slice(0, -1)}ies`;
  }
  if (/(?:[sxz]|[sc]h)$/i.test(label)) {
    return `${label}es`;
  }
  return `${label}s`;
}

/**
 * Get full entity type name for route construction from short name.
 * Single source of truth for short-to-long name mapping.
 */
export function getEntityTypeRoute(
  shortName: 'contact' | 'company' | 'deal'
): string {
  const mapping = {
    contact: 'ahoy_contact',
    company: 'ahoy_company',
    deal: 'ahoy_deal',
  };
  return mapping[shortName];
}

/**
 * Convert entity_type_name (snake_case) to kebab-case URL slug.
 * @example toKebabSlug('org_my_entity') // 'org-my-entity'
 * @example toKebabSlug('ahoy_contact') // 'ahoy-contact'
 */
export function toKebabSlug(entityTypeName: string): string {
  return entityTypeName.replace(/_/g, '-');
}

/**
 * Map entity_type_name to the canonical CRM route slug.
 * Built-in types use their plural short names (contacts, companies, deals)
 * to match dedicated routes. Custom types use kebab-cased names.
 */
const BUILTIN_ROUTE_SLUGS: Record<string, string> = {
  ahoy_contact: 'contacts',
  ahoy_company: 'companies',
  ahoy_deal: 'deals',
};

export function entityTypeToRouteSlug(entityTypeName: string): string {
  return BUILTIN_ROUTE_SLUGS[entityTypeName] ?? toKebabSlug(entityTypeName);
}

/**
 * Convert kebab-case URL slug back to entity_type_name (snake_case).
 * @example toSnakeCase('org-my-entity') // 'org_my_entity'
 * @example toSnakeCase('ahoy-contact') // 'ahoy_contact'
 */
export function toSnakeCase(slug: string): string {
  return slug.replace(/-/g, '_');
}
